import { ApolloQueryResult } from 'apollo-client';
import { EnterPasswordForm } from 'components/authentication/EnterPasswordForm';
import { AuthenticationField, AuthenticationModalType } from 'components/authentication/types';
import { flow } from 'lodash';
import React, { Dispatch, useState } from 'react';
import { connect } from 'react-redux';
import { AuthError, IGenerateEmailVerificationCodeVariables, ILoginWithEmailAndPasswordVariables, MutationResponseByType, MutationType } from 'store/sagas/apiService/types';
import { RootAction, State } from 'store/state';
import { setInvestorsLoginStep, userLoginWithEmailAndPassword } from 'store/state/app/actions';
import { authEmailSelector, investorsLoginSourceVariablesSelector } from 'store/state/app/selectors';
import { userAuthErrorSelector } from 'store/state/domainData/selectors';
import { inlineMutate } from 'store/state/mutationsResponse/actions';
import { loginWithEmailAndPasswordDataSelector } from 'store/state/mutationsResponse/selectors';

interface EnterPasswordProps {
  email: string;
  authModalError: boolean;
  isLoginFailed: boolean;
  loginEmailPasswordResponse?: ApolloQueryResult<MutationResponseByType[MutationType.LoginWithEmailAndPassword]>;
  onSetActiveModal: (nextType: AuthenticationModalType) => void;
  onLoginWithEmailAndPassword: (data: ILoginWithEmailAndPasswordVariables) => void;
  generateEmailVerificationCode: (data: IGenerateEmailVerificationCodeVariables) => void;
}

const EnterPasswordBase: React.FC<EnterPasswordProps> = (props) => {
  const {
    email,
    authModalError,
    loginEmailPasswordResponse,
    onSetActiveModal,
    onLoginWithEmailAndPassword,
    generateEmailVerificationCode,
    isLoginFailed,
  } = props;
  const [ password, setPassword ] = useState<string>('');
  const [ dirty, setDirty ] = useState(new Set<AuthenticationField>([]));

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value);
  const handleSetDirty = (field: AuthenticationField) => setDirty(prev => new Set([ ...prev, field ]));

  const onLogin = () => {
    onLoginWithEmailAndPassword({ email: email.toLowerCase(), password });
  };

  return (
    <EnterPasswordForm
      email={email}
      setActiveModal={onSetActiveModal}
      onToggleResetPassword={() => {
        onSetActiveModal(AuthenticationModalType.UserEmailVerification);
        generateEmailVerificationCode({ email });
      }}
      password={password}
      onPasswordChange={handlePasswordChange}
      isLoginFailed={isLoginFailed || authModalError}
      dirtyFields={dirty}
      onSubmit={onLogin}
      onSetDirty={handleSetDirty}
      isLoading={loginEmailPasswordResponse ? loginEmailPasswordResponse.loading : false}
      fullWidth
    />
  );
};

const loginFailedSelectorDirty = flow(userAuthErrorSelector, (e) => e ? e.reason === AuthError.LoginFailed : null);
const authModalErrorSelector = flow(investorsLoginSourceVariablesSelector, (e) => e ? e.error : null);

const mapStateToProps = (state: State) => ({
  email: authEmailSelector(state),
  isLoginFailed: loginFailedSelectorDirty(state),
  authModalError: authModalErrorSelector(state),
  loginEmailPasswordResponse: loginWithEmailAndPasswordDataSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onLoginWithEmailAndPassword: (data: ILoginWithEmailAndPasswordVariables) => dispatch(userLoginWithEmailAndPassword(data)),
  generateEmailVerificationCode: (data: IGenerateEmailVerificationCodeVariables) => dispatch(inlineMutate(MutationType.GenerateEmailVerificationCode, data)),
  onSetActiveModal: (type: AuthenticationModalType) => dispatch(setInvestorsLoginStep({ type }, { isUserInitiated: true })),
});

export const EnterPassword = connect(mapStateToProps, mapDispatchToProps)(EnterPasswordBase);

